import { Link } from "react-router-dom";
import type { Film } from "@/types/film";
import Breadcrumbs from "@/components/base/Breadcrumbs";

interface DetailHeroProps {
  film: Film;
}

export default function DetailHero({ film }: DetailHeroProps) {
  const listPath = film.kind === "movie" ? "/movies" : "/series";
  const listLabel = film.kind === "movie" ? "伦理电影" : "伦理电视剧";

  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <img
        src={film.backdrop}
        alt={`${film.title}剧照`}
        title={`${film.title} - 伦理视频作品详情`}
        className="absolute inset-0 h-full w-full object-cover object-center opacity-45"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/50" />
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-transparent to-primary-950/60" />

      <div className="relative z-10 px-4 py-8 md:px-10 md:py-12">
        <div className="[&_a]:text-background-300 [&_a:hover]:text-accent-400 [&_span]:text-background-400">
          <Breadcrumbs
            items={[
              { label: "首页", to: "/" },
              { label: listLabel, to: listPath },
              { label: film.title },
            ]}
          />
        </div>

        <div className="mt-6 grid grid-cols-[110px_1fr] gap-4 md:grid-cols-[220px_1fr] md:gap-8">
          <div className="overflow-hidden rounded-lg bg-primary-900 ring-1 ring-background-50/15">
            <div className="aspect-[3/4] w-full">
              <img
                src={film.cover}
                alt={`${film.title}封面`}
                title={`${film.title} - 伦理视频`}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-background-200">
              <span className="rounded-full bg-accent-500 px-2.5 py-0.5 font-medium text-background-50">
                {film.kind === "movie" ? "伦理电影" : "伦理电视剧"}
              </span>
              <span className="rounded-full border border-background-50/30 px-2.5 py-0.5">{film.region}</span>
              <span className="rounded-full border border-background-50/30 px-2.5 py-0.5">{film.year}</span>
              {film.episodes && (
                <span className="rounded-full border border-background-50/30 px-2.5 py-0.5">
                  全 {film.episodes} 集
                </span>
              )}
            </div>

            <h1 className="mt-3 font-heading text-2xl font-semibold leading-tight text-background-50 md:text-4xl">
              {film.title}
            </h1>
            <p className="mt-1.5 text-xs italic tracking-wide text-background-300 md:text-sm">
              {film.originalTitle}
            </p>
            <p className="mt-3 font-heading text-sm text-accent-300 md:text-base">{film.tagline}</p>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-background-200 md:text-sm">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-star-line text-background-400" />
                导演 {film.director}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-background-400" />
                {film.duration}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line text-background-400" />
                {(film.views / 10000).toFixed(1)} 万浏览
              </span>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
              {film.genres.map((genre) => (
                <span
                  key={genre}
                  className="rounded-sm bg-background-50/10 px-2 py-0.5 text-[11px] text-background-100 ring-1 ring-background-50/15"
                >
                  {genre}
                </span>
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="flex items-center gap-3 rounded-md bg-background-50/10 px-4 py-2 ring-1 ring-background-50/15">
                <span className="font-heading text-3xl font-semibold text-accent-400">{film.rating.toFixed(1)}</span>
                <span className="text-[11px] leading-tight text-background-300">
                  站内评分
                  <br />
                  {film.votes.toLocaleString()} 人参与
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-2.5">
                <a
                  href="#reviews"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm text-background-50 transition-colors hover:bg-accent-600 cursor-pointer"
                >
                  <i className="ri-quill-pen-line" />
                  阅读影评
                </a>
                <Link
                  to={listPath}
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/35 px-4 py-2.5 text-sm text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer"
                >
                  <i className="ri-arrow-left-line" />
                  返回{listLabel}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}